import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sun, Moon, ChevronDown } from "lucide-react";

const AboutUs = () => {
  const navigate = useNavigate();
  const navRef = useRef(null);

  const [darkMode, setDarkMode] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({
    features: false,
    resources: false,
    company: false,
  });

  useEffect(() => {
    const isDark = localStorage.getItem("darkMode") === "true";
    setDarkMode(Boolean(isDark));
    if (isDark) document.documentElement.classList.add("dark");
  }, []);

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem("darkMode", next.toString());
    if (next) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  };

  const toggleDropdown = (menu) => {
    setDropdownOpen((prev) => ({
      features: false,
      resources: false,
      company: false,
      [menu]: !prev[menu],
    }));
  };

  const closeAllDropdowns = () =>
    setDropdownOpen({ features: false, resources: false, company: false });

  useEffect(() => {
    const onClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target))
        closeAllDropdowns();
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const dropdownMenus = {
    features: [
      { name: "Paper Analysis", link: "/", description: "Analyze research papers" },
      { name: "Code Generation", link: "/", description: "Generate code from papers" },
      { name: "Architecture Diagrams", link: "/", description: "Visualize systems" },
    ],
    resources: [
      { name: "Documentation", link: "/help", description: "Complete guides" },
      { name: "Help Center", link: "/help", description: "Get support" },
      { name: "API Reference", link: "/help", description: "Developer docs" },
      { name: "Community", link: "/help", description: "Join discussions" },
    ],
    company: [
      { name: "About Us", link: "/about", description: "Our story" },
      { name: "Contact", link: "/contact", description: "Get in touch" },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* NAVBAR */}
      <nav
        ref={navRef}
        className="bg-white dark:bg-gray-800 shadow-md sticky top-0 z-50 w-full"
      >
        <div className="flex items-center justify-between h-16 w-full px-6 lg:px-12">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center text-white font-bold">
                P
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                PaperMind AI
              </span>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-1">
              <div className="relative">
                <button
                  onClick={() => toggleDropdown("features")}
                  className="flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="text-gray-700 dark:text-gray-300">Features</span>
                  <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>
                {dropdownOpen.features && (
                  <div className="absolute right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50 w-auto min-w-max">
                    {dropdownMenus.features.map((item, i) => (
                      <Link
                        key={i}
                        to={item.link}
                        className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {item.name}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => toggleDropdown("resources")}
                  className="flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="text-gray-700 dark:text-gray-300">Resources</span>
                  <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>
                {dropdownOpen.resources && (
                  <div className="absolute right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50 w-auto min-w-max">
                    {dropdownMenus.resources.map((item, i) => (
                      <Link
                        key={i}
                        to={item.link}
                        className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {item.name}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => toggleDropdown("company")}
                  className="flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="text-gray-700 dark:text-gray-300">Company</span>
                  <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>
                {dropdownOpen.company && (
                  <div className="absolute right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50 w-auto min-w-max">
                    {dropdownMenus.company.map((item, i) => (
                      <Link
                        key={i}
                        to={item.link}
                        className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {item.name}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/pricing"
                className="px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
              >
                Pricing
              </Link>
            </div>

            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle dark mode"
            >
              {darkMode ? (
                <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              )}
            </button>

            <button onClick={() => navigate("/login")} className="btn-secondary text-sm">
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* MAIN CONTENT */}
      <div className="container mx-auto px-6 py-16 max-w-4xl">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-8">
          About PaperMind AI
        </h1>

        <div className="card p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Our Mission
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            PaperMind AI transforms complex research papers into actionable code and insights in minutes. 
            We believe groundbreaking research should be accessible to everyone.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            Our platform uses AI-powered agents to automatically analyze papers, extract key insights, 
            generate architecture diagrams, and produce production-ready code.
          </p>
        </div>

        <div className="card p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            What We Do
          </h2>
          <ul className="space-y-3 text-gray-700 dark:text-gray-300">
            <li className="flex items-start">
              <span className="text-primary-600 mr-2">•</span>
              <span>Analyze research papers and extract key information</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-600 mr-2">•</span>
              <span>Generate architecture diagrams and visual representations</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-600 mr-2">•</span>
              <span>Create runnable code implementations from papers</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-600 mr-2">•</span>
              <span>Save researchers hours of manual work</span>
            </li>
          </ul>
        </div>

        <div className="card p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            The Team
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            PaperMind AI was built by a team of passionate developers during a hackathon.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl mb-2">🧑‍💻</div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Rachit Kulkarni</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Backend Lead</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-2">👩‍💻</div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Shreyas BS</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Frontend Lead</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-2">🧑‍🔧</div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Dheemanth Raviraj</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Infrastructure Lead</p>
            </div>
          </div>
        </div>

        <div className="card p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Technology
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Built with modern technologies:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold text-primary-600 dark:text-primary-400 mb-2">
                Frontend
              </h3>
              <p className="text-gray-700 dark:text-gray-300">React, Tailwind CSS</p>
            </div>
            <div>
              <h3 className="font-semibold text-primary-600 dark:text-primary-400 mb-2">
                Backend
              </h3>
              <p className="text-gray-700 dark:text-gray-300">FastAPI, Celery, Redis</p>
            </div>
            <div>
              <h3 className="font-semibold text-primary-600 dark:text-primary-400 mb-2">
                AI
              </h3>
              <p className="text-gray-700 dark:text-gray-300">Claude API</p>
            </div>
            <div>
              <h3 className="font-semibold text-primary-600 dark:text-primary-400 mb-2">
                Database
              </h3>
              <p className="text-gray-700 dark:text-gray-300">Supabase</p>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <button
            onClick={() => navigate("/signup")}
            className="btn-primary px-8 py-3"
          >
            Get Started
          </button>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="bg-gray-100 dark:bg-gray-900 py-8 border-t border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4 text-center text-gray-600 dark:text-gray-400">
          <p>© 2025 PaperMind AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;